import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { apiErrorCode, apiErrorMessage } from "@/api/client";

export function LoginPage() {
  const { login, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const explicitFrom = (location.state as { from?: string } | null)?.from;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const target = explicitFrom ?? (user?.role === "admin" ? "/admin/orders" : "/catalog");
    return <Navigate to={target} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await login(email.trim(), password);
      const dest =
        explicitFrom ?? (resp.user.role === "admin" ? "/admin/orders" : "/catalog");
      navigate(dest, { replace: true });
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "ACCOUNT_LOCKED") {
        setError("連続ログイン失敗によりアカウントがロックされています。しばらくしてから再度お試しください。");
      } else if (code === "INVALID_CREDENTIALS") {
        setError("メールアドレスまたはパスワードが正しくありません。");
      } else {
        setError(apiErrorMessage(err, "ログインに失敗しました"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-brand-700">かんたん在庫</h1>
          <p className="mt-2 text-sm text-gray-600">
            越境ECセラー向けB2B仕入れプラットフォーム
          </p>
        </div>

        <form onSubmit={onSubmit} className="card p-6 space-y-4">
          <h2 className="text-xl font-semibold">ログイン</h2>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label className="label" htmlFor="email">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              パスワード
            </label>
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
            />
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? "ログイン中..." : "ログイン"}
          </button>

          <div className="text-sm text-center">
            <Link to="/forgot-password" className="text-brand-700 hover:underline">
              パスワードをお忘れの方
            </Link>
          </div>

          <div className="text-sm pt-3 border-t text-center">
            <span className="text-gray-600">ご利用開始をご希望の方は </span>
            <Link to="/apply" className="text-brand-700 font-medium hover:underline">
              こちらから新規お申込み
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
